import type { MessageSegment } from "./types"

export type SourceMetadata = {
  sourceId: string
  citation?: string
  title?: string
  publicationYear?: string | number
}

export type DeepLiteratureEvidenceMarker =
  | {
      kind: "chunk"
      sourceId: string
      chunkId: string
      page: string
    }
  | {
      kind: "table-cell"
      sourceId: string
      page: string
      tableId: string
      rowIndex: string
      columnIndex: string
    }

export type DeepLiteratureEvidenceChunk = {
  sourceId: string
  chunkId: string
  page?: string
  content: string
  contentKind?: string
  headings?: string[]
  citation?: string
  title?: string
  publicationYear?: string | number
  contentTruncated?: boolean
}

type EvidenceReference = {
  sourceId: string
  pages: string[]
}

const EVIDENCE_MARKER_PATTERN = /\[evidence:(chunk|table-cell):([^\]]+)\]/g
const PARTIAL_EVIDENCE_MARKER_PATTERN = /\[evidence:(?:chunk|table-cell):[^\]]*$/
const DEEP_LITERATURE_EVIDENCE_HREF_PREFIX = "#deep-literature-evidence?"
const BIBLIO_SOURCE_MARKER_PATTERN = /\[\[(\d+)\]\](?!\()/g

function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

function textValue(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined
  const trimmed = value.trim()
  return trimmed || undefined
}

function stringValue(value: unknown): string | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return String(value)
  return textValue(value)
}

function pageValue(value: unknown): string | undefined {
  const valueText = stringValue(value)
  if (!valueText) return undefined
  return valueText.startsWith("p") ? valueText.slice(1) : valueText
}

function publicationYearValue(value: unknown): string | number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value
  return textValue(value)
}

function contentValue(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined
  const trimmed = value.trim()
  return trimmed || undefined
}

function stringArrayValue(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined
  const strings = value.flatMap(item => {
    const text = textValue(item)
    return text ? [text] : []
  })
  return strings.length ? strings : undefined
}

function booleanValue(value: unknown): boolean | undefined {
  return typeof value === "boolean" ? value : undefined
}

function parseJsonResult(result?: string): unknown {
  if (!result) return null
  try {
    return JSON.parse(result)
  } catch {
    return null
  }
}

function isDescribeBiblioSourcesTool(name: string): boolean {
  return name === "describe_biblio_sources" || name.endsWith("___describe_biblio_sources")
}

function isDiscoverBiblioSourcesTool(name: string): boolean {
  return (
    name === "discover_biblio_sources_by_content" ||
    name.endsWith("___discover_biblio_sources_by_content")
  )
}

function isChunkTextTool(name: string): boolean {
  return (
    name === "search_biblio_sources_contents" ||
    name.endsWith("___search_biblio_sources_contents") ||
    name === "get_biblio_chunk_context" ||
    name.endsWith("___get_biblio_chunk_context")
  )
}

function addPage(reference: EvidenceReference, page: string) {
  if (page && !reference.pages.includes(page)) {
    reference.pages.push(page)
  }
}

function ensureReference(references: Map<string, EvidenceReference>, sourceId: string): EvidenceReference {
  const reference = references.get(sourceId) ?? {
    sourceId,
    pages: [],
  }
  references.set(sourceId, reference)
  return reference
}

function collectBiblioSourceMarkerIds(text: string): string[] {
  const sourceIds: string[] = []

  for (const match of text.matchAll(BIBLIO_SOURCE_MARKER_PATTERN)) {
    const sourceId = match[1]
    if (sourceId && !sourceIds.includes(sourceId)) {
      sourceIds.push(sourceId)
    }
  }

  return sourceIds
}

export function parseEvidenceMarker(
  kind: string,
  payload: string
): DeepLiteratureEvidenceMarker | null {
  const parts = payload.split(":")
  if (kind === "chunk" && parts.length >= 3) {
    const [sourceId, chunkId, pageToken] = parts
    if (!sourceId || !chunkId || !pageToken?.startsWith("p")) return null
    return {
      kind: "chunk",
      sourceId,
      chunkId,
      page: pageToken.slice(1),
    }
  }
  if (kind === "table-cell" && parts.length >= 5) {
    const [sourceId, pageToken, tableId, rowToken, columnToken] = parts
    if (
      !sourceId ||
      !tableId ||
      !pageToken?.startsWith("p") ||
      !rowToken?.startsWith("r") ||
      !columnToken?.startsWith("c")
    ) {
      return null
    }
    return {
      kind: "table-cell",
      sourceId,
      page: pageToken.slice(1),
      tableId,
      rowIndex: rowToken.slice(1),
      columnIndex: columnToken.slice(1),
    }
  }
  return null
}

function collectEvidenceReferences(texts: string[]): EvidenceReference[] {
  const references = new Map<string, EvidenceReference>()

  for (const text of texts) {
    for (const match of text.matchAll(EVIDENCE_MARKER_PATTERN)) {
      const marker = parseEvidenceMarker(match[1], match[2])
      if (!marker) continue

      const reference = ensureReference(references, marker.sourceId)
      addPage(reference, marker.page)
    }

    for (const sourceId of collectBiblioSourceMarkerIds(text)) {
      ensureReference(references, sourceId)
    }
  }

  return [...references.values()]
}

export function collectBiblioSourceMetadata(segments?: MessageSegment[]): Map<string, SourceMetadata> {
  const metadata = new Map<string, SourceMetadata>()

  // Retrieval metadata is a useful fallback for older results. Process the
  // authoritative describe result last so its fields win when both exist.
  for (const describePass of [false, true]) {
    for (const segment of segments ?? []) {
      if (segment.type !== "tool") continue

      const toolName = segment.toolCall.name
      const isDescribe = isDescribeBiblioSourcesTool(toolName)
      if (isDescribe !== describePass) continue

      const result = asRecord(parseJsonResult(segment.toolCall.result))
      const sources =
        isDescribe || isDiscoverBiblioSourcesTool(toolName)
          ? result?.sources
          : isChunkTextTool(toolName)
            ? result?.chunks
            : undefined
      if (!Array.isArray(sources)) continue

      for (const source of sources) {
        const sourceRecord = asRecord(source)
        if (!sourceRecord) continue

        const sourceId = stringValue(sourceRecord.sourceId)
        if (!sourceId) continue

        const next = metadata.get(sourceId) ?? { sourceId }
        const citation = textValue(sourceRecord.citation)
        const title = textValue(sourceRecord.title)
        const publicationYear = publicationYearValue(sourceRecord.publicationYear)
        if (citation !== undefined) next.citation = citation
        if (title !== undefined) next.title = title
        if (publicationYear !== undefined) next.publicationYear = publicationYear
        metadata.set(sourceId, next)
      }
    }
  }

  return metadata
}

function chunkFromRecord(record: Record<string, unknown>): DeepLiteratureEvidenceChunk | null {
  const sourceId = stringValue(record.sourceId)
  const chunkId = stringValue(record.chunkId)
  const content = contentValue(record.content)
  if (!sourceId || !chunkId || !content) return null

  return {
    sourceId,
    chunkId,
    page: pageValue(record.page),
    content,
    contentKind: textValue(record.contentKind),
    headings: stringArrayValue(record.headings),
    citation: textValue(record.citation),
    title: textValue(record.title),
    publicationYear: publicationYearValue(record.publicationYear),
    contentTruncated: booleanValue(record.contentTruncated),
  }
}

export function evidenceChunkKey(sourceId: string, chunkId: string): string {
  return `${sourceId}:${chunkId}`
}

export function collectDeepLiteratureEvidenceChunks(
  segments?: MessageSegment[]
): Map<string, DeepLiteratureEvidenceChunk> {
  const chunks = new Map<string, DeepLiteratureEvidenceChunk>()

  for (const segment of segments ?? []) {
    if (segment.type !== "tool" || !isChunkTextTool(segment.toolCall.name)) continue

    const result = asRecord(parseJsonResult(segment.toolCall.result))
    const resultChunks = result?.chunks
    if (!Array.isArray(resultChunks)) continue

    for (const resultChunk of resultChunks) {
      const chunkRecord = asRecord(resultChunk)
      if (!chunkRecord) continue

      const chunk = chunkFromRecord(chunkRecord)
      if (!chunk) continue

      chunks.set(evidenceChunkKey(chunk.sourceId, chunk.chunkId), chunk)
    }
  }

  return chunks
}

function evidenceLabel(marker: DeepLiteratureEvidenceMarker): string {
  return `${marker.sourceId}, p. ${marker.page}`
}

function evidenceHref(marker: DeepLiteratureEvidenceMarker): string {
  const params = new URLSearchParams()
  params.set("kind", marker.kind)
  params.set("sourceId", marker.sourceId)
  params.set("page", marker.page)

  if (marker.kind === "chunk") {
    params.set("chunkId", marker.chunkId)
  } else {
    params.set("tableId", marker.tableId)
    params.set("rowIndex", marker.rowIndex)
    params.set("columnIndex", marker.columnIndex)
  }

  return `${DEEP_LITERATURE_EVIDENCE_HREF_PREFIX}${params.toString()}`
}

export function parseDeepLiteratureEvidenceHref(
  href?: string
): DeepLiteratureEvidenceMarker | null {
  if (!href?.startsWith(DEEP_LITERATURE_EVIDENCE_HREF_PREFIX)) return null

  const params = new URLSearchParams(href.slice(DEEP_LITERATURE_EVIDENCE_HREF_PREFIX.length))
  const kind = params.get("kind")
  const sourceId = params.get("sourceId")
  const page = params.get("page")
  if (!kind || !sourceId || !page) return null

  if (kind === "chunk") {
    const chunkId = params.get("chunkId")
    return chunkId ? { kind, sourceId, page, chunkId } : null
  }

  if (kind === "table-cell") {
    const tableId = params.get("tableId")
    const rowIndex = params.get("rowIndex")
    const columnIndex = params.get("columnIndex")
    if (!tableId || !rowIndex || !columnIndex) return null
    return { kind, sourceId, page, tableId, rowIndex, columnIndex }
  }

  return null
}

function removeStandaloneSourceReferenceLines(text: string): string {
  return text
    .split("\n")
    .filter(line => {
      const withoutSourceReferences = line
        .trim()
        .replace(BIBLIO_SOURCE_MARKER_PATTERN, "")
        .replace(/[\s,;]+/g, "")
      return withoutSourceReferences.length > 0
    })
    .join("\n")
}

export function formatDeepLiteratureEvidenceText(text: string): string {
  const withEvidenceLinks = removeStandaloneSourceReferenceLines(text)
    .replace(EVIDENCE_MARKER_PATTERN, (_match, kind: string, payload: string) => {
      const marker = parseEvidenceMarker(kind, payload)
      return marker ? `[${evidenceLabel(marker)}](${evidenceHref(marker)})` : ""
    })
    .replace(PARTIAL_EVIDENCE_MARKER_PATTERN, "")
    .replace(BIBLIO_SOURCE_MARKER_PATTERN, (_match, sourceId: string) =>
      biblioSourceMarkdownLink(sourceId)
    )
    .replace(/[ \t]+([.,;:!?])/g, "$1")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/^[ \t]+$/gm, "")
    .replace(/\n{3,}/g, "\n\n")

  return withEvidenceLinks
}

function fallbackCitation(metadata: SourceMetadata | undefined, sourceId: string): string {
  if (metadata?.citation) return metadata.citation

  const title = metadata?.title
  const publicationYear = metadata?.publicationYear
  if (title && publicationYear) return `${title.replace(/\.$/, "")}. ${publicationYear}.`
  if (title) return title

  return `Source ${sourceId}`
}

function formatPages(pages: string[]): string {
  if (pages.length === 0) return ""
  const sortedPages = [...pages].sort((left, right) => {
    const leftStart = Number.parseInt(left, 10)
    const rightStart = Number.parseInt(right, 10)
    if (Number.isNaN(leftStart) || Number.isNaN(rightStart)) {
      return left.localeCompare(right)
    }
    return leftStart - rightStart
  })
  const pageLabel = pages.length === 1 && !pages[0].includes("-") ? "p." : "pp."
  return `, ${pageLabel} ${sortedPages.join(", ")}`
}

function biblioSourceMarkdownLink(sourceId: string): string {
  return `[[${sourceId}]](/map-biblio/sources/${encodeURIComponent(sourceId)})`
}

export function buildDeepLiteratureReferencesMarkdown(
  texts: string[],
  sourceMetadata: Map<string, SourceMetadata>
): string | null {
  const references = collectEvidenceReferences(texts)
  if (references.length === 0) return null

  const lines = references.map(reference => {
    const citation = fallbackCitation(sourceMetadata.get(reference.sourceId), reference.sourceId)
    return `- ${citation} ${biblioSourceMarkdownLink(reference.sourceId)}${formatPages(reference.pages)}`
  })

  return `### References\n\n${lines.join("\n")}`
}
